const API = process.env.REACT_APP_DIRECTUS_URL;

async function jfetch(path, opts = {}, isRetry = false) {
    const token = localStorage.getItem('directus_access_token');

    const headers = {
        'Content-Type': 'application/json',
        ...(opts.headers || {})
    };

    // Add Bearer token if available
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(`${API}${path}`, {
        credentials: 'include', // Still send cookies for refresh token
        headers,
        ...opts,
    });

    // If we get 401 and haven't already retried, try to refresh token
    if (res.status === 401 && !isRetry && path !== '/auth/refresh') {
        try {
            console.log('Token expired, attempting refresh...');
            await refreshAccessToken();
            // Retry the original request with new token
            return jfetch(path, opts, true);
        } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // Clear invalid tokens and redirect to login
            localStorage.removeItem('directus_access_token');
            throw new Error('Session expired. Please log in again.');
        }
    }

    if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `HTTP ${res.status}`);
    }

    const text = await res.text();
    try { return text ? JSON.parse(text) : null; } catch { return null; }
}

// New function to refresh the access token
async function refreshAccessToken() {
    const res = await fetch(`${API}/auth/refresh`, {
        method: 'POST',
        credentials: 'include', // This sends the httpOnly refresh token
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
    });

    if (!res.ok) {
        throw new Error('Refresh failed');
    }

    const data = await res.json();

    if (data?.data?.access_token) {
        localStorage.setItem('directus_access_token', data.data.access_token);
        console.log('Token refreshed successfully');
        return data.data.access_token;
    } else {
        throw new Error('No access token in refresh response');
    }
}

// Accept invitation and set password
export async function acceptInvite(token, password) {
    const res = await fetch(`${API}/users/invite/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            token,
            password
        })
    });

    if (!res.ok) {
        const error = await res.text();
        throw new Error(error || 'Invitation acceptance failed');
    }

    return res.json();
}

export async function loginDirectus(email, password) {
    const res = await jfetch('/auth/login', {
        method: 'POST',
        body: JSON.stringify({
            email,
            password,
            mode: 'cookie'
        })
    });

    // Store the access token for immediate use
    if (res?.data?.access_token) {
        localStorage.setItem('directus_access_token', res.data.access_token);
    }

    return getMe();
}

export async function logoutDirectus() {
    try {
        await jfetch('/auth/logout', {
            method: 'POST',
            body: JSON.stringify({})
        });
        console.log("Logout successful");
    } catch (err) {
        console.error("Logout failed:", err);
    }

    // IMPORTANT: Clear the access token from localStorage
    localStorage.removeItem('directus_access_token');

    // Also clear any other auth-related items you might have
    localStorage.removeItem('directus_refresh_token'); // if you have this
}

export async function getMe() {
    const data = await jfetch('/users/me?fields=id,email,first_name,last_name,role');
    return data?.data ?? null;
}

// example: your 1–1 account row linked by `user` field
// export async function getMyAccount() {
//     const me = await getMe();
//     if (!me) return null;
//     const q = new URLSearchParams({
//         'filter[user][_eq]': me.id,
//         'limit': '1',
//         'fields': 'id'
//     });
//     const res = await jfetch(`/items/accounts?${q.toString()}`);
//     return res?.data?.[0] ?? null;
// }

export async function getMyRestaurants() {
    const me = await getMe();
    console.log(me);
    if (!me) return [];

    // Query the junction table, then get the related restaurants
    const q = new URLSearchParams({
        'filter[user][_eq]': me.id,
        // 'fields': 'restaurant.id,restaurant.name,restaurant.subscription_status'
        'fields': 'restaurant.id,restaurant.name,restaurant.stripe_customer_id,restaurant.stripe_subscription_id,restaurant.subscription_status,restaurant.current_plan'
    });
    const res = await jfetch(`/items/restaurant_users?${q.toString()}`);

    // Extract just the restaurant objects
    return res?.data?.map(item => item.restaurant) ?? [];
}
